/* eslint-disable */
// @ts-nocheck
/* Legacy imports */
import { LuceneResult } from '../../../../../../../common/components/core/LuceneQueryBuilder/LuceneResult';
import {
  assertNonInvertible,
  flatASTToLuceneString,
  flattenAST,
  luceneParse,
  simplifyFlatAST,
} from '../../../../../../../common/components/core/LuceneQueryBuilder/luceneTransform';
import { deriveField, deriveTerm } from '../../../../../../../common/components/core/LuceneQueryBuilder/luceneSelectors';
import QueryPartState from '../../../../../../../common/components/core/LuceneQueryBuilder/QueryPartState';
import * as vendorHelpers from '../../../../../../../../../shared/vendorHelpers';
import { COMMERCE_EVENT_NAMES } from '../../../../../../../common/support/eo_mapping';
import { getCustomEventName } from '../../../../../../../common/support/utils/eo_helpers';

/* Non-legacy imports */
import { AccessResourcePermissions, FlatAst } from '../PostbackDataMapper.types';
import { AgencyAccessFilters } from './FilterService.types';
import { BasicOption, EventOption, Option } from '../../PostbackForm.types';
import PostbackDataMapperException from '../../exceptions/PostbackDataMapperException';
import { FREQUENCY_FILTER, eventNameWithDelimiterRegex } from './FilterService.constants';

export const parseFlattenSimplifyAndValidateLucene = (enablePrefix: boolean, queryString?: string): FlatAst[] | Error => {
  const flattened: LuceneResult = LuceneResult.ok(queryString).map(luceneParse).map(flattenAST);
  const flattenedError: Error | undefined = flattened.getError();
  if (flattenedError) {
    return flattenedError;
  }

  const validationResult1: LuceneResult = flattened.map(flatASTToLuceneString).map(assertNonInvertible);
  const validationResult1Error: Error | undefined = validationResult1.getError();
  if (validationResult1Error) {
    return validationResult1Error;
  }

  const simplifiedFlatAST: LuceneResult = flattened.map((flatAst) => simplifyFlatAST(flatAst, enablePrefix));

  const validationResult2 = simplifiedFlatAST.map(flatASTToLuceneString).map(assertNonInvertible);
  const validationResult2Error: Error | undefined = validationResult2.getError();
  if (validationResult2Error) {
    return validationResult2Error;
  }

  return simplifiedFlatAST.getData() || [];
};

interface RemoveDefaultQueriesResult {
  removedQueries: FlatAst[];
  flatAstWithoutDefaults: FlatAst[];
}

export const removeDefaultQueries = (flatAst: FlatAst[], filterDisplayKeys?: string[]): RemoveDefaultQueriesResult => {
  const removedQueries: FlatAst[] = [];
  const flatAstWithoutDefaults = [...flatAst];
  const wildCardIndex = flatAst.findIndex((part) => deriveField(part) === '*' && deriveTerm(part) === '*');

  if (wildCardIndex > -1) {
    removedQueries.push(flatAst[wildCardIndex]);
    flatAstWithoutDefaults.splice(wildCardIndex, 1);
  }

  if (filterDisplayKeys && filterDisplayKeys.length > 0) {
    filterDisplayKeys.forEach((filterKey) => {
      const filterKeyIndex = flatAstWithoutDefaults.findIndex((element) => deriveField(element) === filterKey);
      if (filterKeyIndex > -1) {
        removedQueries.push(flatAstWithoutDefaults[filterKeyIndex]);
        flatAstWithoutDefaults.splice(filterKeyIndex, 1);
      }
    });
  }

  return { removedQueries, flatAstWithoutDefaults };
};

export const getAccessFilterForKey = (key?: string, accessFilters?: AgencyAccessFilters | null): string[] | undefined =>
  key ? accessFilters?.dropdown_options[key] : undefined;

export const getAccessFilters = (
  accessControlData?: AccessResourcePermissions,
  agencyUser?: boolean,
  agencyId?: string
): AgencyAccessFilters | null => {
  if (!agencyUser || !accessControlData) {
    return null;
  }

  const accessFilters = vendorHelpers.getFiltersForDegradedPermissions(accessControlData, agencyUser, agencyId);
  if (!accessFilters || Object.keys(accessFilters).length === 0) {
    return null;
  }
  return {
    filter_string: vendorHelpers.applyAccessControlsToWebhook('', accessFilters),
    dropdown_options: accessFilters,
  };
};

export const FALLBACK_OPTION = {
  label: 'Custom Key',
  value: '_custom_',
};

export const getSortedOptions = (options: BasicOption[]) => {
  const sortFn = (a: BasicOption, b: BasicOption) => a.label.localeCompare(b.label);
  const customIndex = options.findIndex((option) => option.value === FALLBACK_OPTION.value);

  if (customIndex < 0) {
    return options.sort(sortFn);
  }

  const optionsWithoutCustom = [...options];
  const [customOption] = optionsWithoutCustom.splice(customIndex, 1);
  const sortedOptions = optionsWithoutCustom.sort(sortFn);
  return [...sortedOptions, customOption];
};

export const getOptionObjectForField = (field: string, options: BasicOption[]): BasicOption =>
  options.find((option) => option.value === field) || FALLBACK_OPTION;

export const DISABLED_FIELDS_POSTBACKS = [vendorHelpers.AGENCY_TAGGED_DATA_FIELD, vendorHelpers.AD_NETWORKS_FIELD];
export const DISABLED_FIELDS_WEBHOOKS = [vendorHelpers.AGENCY_TAGGED_DATA_FIELD];

export const areTermsNonEditable = (field: string, isWebhook: boolean, disableForAgencyUser: boolean): boolean => {
  if (!disableForAgencyUser) {
    return false;
  }

  return isWebhook ? DISABLED_FIELDS_WEBHOOKS.includes(field) : DISABLED_FIELDS_POSTBACKS.includes(field);
};

export const getDropdownValue = (terms: string[], isSingleValueDropdown?: boolean): Option | Option[] | undefined => {
  const values: Option[] = terms
    .filter((term) => !!term)
    .map((term) => ({
      label: term,
      value: term,
    }));

  if (isSingleValueDropdown) {
    const [singleValue] = values;
    return singleValue;
  }

  return values;
};

export const getAccessFilterOptions = (field: string, accessFilters?: AgencyAccessFilters | null): Option[] | undefined => {
  const accessFilterKey: string | undefined = vendorHelpers.protectedLuceneFieldsToPermissionKeys[field];
  const accessFilter = getAccessFilterForKey(accessFilterKey, accessFilters);

  return (
    accessFilter &&
    accessFilter.map((value) => {
      return {
        label: value,
        value,
      };
    })
  );
};

export const getOptions = (
  field: string,
  options: BasicOption[],
  queryValueOptions?: { [key: string]: string[] },
  accessFilters?: AgencyAccessFilters | null
): Option[] => {
  const accessFilterOptions = getAccessFilterOptions(field, accessFilters);
  if (accessFilterOptions) {
    return accessFilterOptions;
  }

  const option = getOptionObjectForField(field, options);
  const defaultOptions = option.config && option.config.defaultQueryValueOptions;
  const optionsResult = (queryValueOptions && queryValueOptions[field]) || defaultOptions || undefined;

  return optionsResult
    ? optionsResult.map((value) => ({
        label: value,
        value,
      }))
    : [];
};

export const convertToQuery = (queryParts: QueryPartState[], removedQueries: FlatAst[]): string => {
  const hasIncompleteQueryPart = queryParts.some((queryPart) => !queryPart.toFlatAst());
  const mergedQueryParts: QueryPartState[] = QueryPartState.consolidateQueryPartStates(queryParts);
  const flatAsts: FlatAst[] = QueryPartState.toFlatAsts(mergedQueryParts);

  if (hasIncompleteQueryPart || !flatAsts) {
    throw new PostbackDataMapperException('One or more rows are incomplete, please update query.');
  }

  const filterQuery: LuceneResult = LuceneResult.ok(removedQueries.concat(flatAsts)).map(flatASTToLuceneString);
  const validationResultError: Error | undefined = filterQuery.map(assertNonInvertible).getError();

  if (validationResultError) {
    throw new PostbackDataMapperException(validationResultError.message);
  }

  const filterQueryValue: string | Error = filterQuery.value();
  if (filterQueryValue instanceof Error) {
    throw new PostbackDataMapperException(filterQueryValue.message);
  }

  return filterQueryValue;
};

export const commerceEventKeys = Object.keys(COMMERCE_EVENT_NAMES).filter((name) => name !== 'revenue');

export function getFilterWithFrequency(filter: string, value: 'first_time' | 'every_time') {
  // remove all frequency filters to reduce complexity
  filter = filter.replace(new RegExp(`${FREQUENCY_FILTER}( AND )?`, 'i'), '');

  if (value === 'every_time') {
    return filter;
  }
  // prepend frequency filter
  return filter.length ? `${FREQUENCY_FILTER} AND ${filter}` : FREQUENCY_FILTER;
}

export const isFirstTimeCompatible = (event: string, customEventList: string[]) => {
  const firstTimeCompatibleEvents = [
    ...['INSTALL', 'OPEN'],
    ...customEventList.map(getCustomEventName),
    ...commerceEventKeys,
  ];
  return firstTimeCompatibleEvents.includes(event);
};

export const getAdjustedFilter = (eventOption: EventOption, filterQuery: string, customEventList: string[]) => {
  let adjustedFilterQuery = filterQuery.replace(eventNameWithDelimiterRegex, '');

  if (!isFirstTimeCompatible(eventOption.value, customEventList)) {
    adjustedFilterQuery = getFilterWithFrequency(adjustedFilterQuery, 'every_time');
  }

  if (eventOption.value !== eventOption.event_datasource) {
    adjustedFilterQuery = `name:"${eventOption.value}" AND ${adjustedFilterQuery}`;
  }

  return adjustedFilterQuery;
};
